import { create } from "zustand";
import { apiClient } from '../api';
import { useAuthStore } from './authStore';

/**
 * Zustand store for managing bookmarks and annotations
 */
export const useBookmarkStore = create((set, get) => {
  return {
    // Array of bookmarks
    // Structure: { id, topicId, pageId, resourceId, title, createdAt, pageNumber }
    bookmarks: [],

    // Array of annotations
    // Structure: { id, topicId, pageId, title, content, createdAt, pageNumber }
    annotations: [],

    /**
     * Add a bookmark
     * @param {Object} bookmark - Bookmark data
     */
    addBookmark: async (bookmark) => {
      try {
        const generateUUID = () => {
          return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            const r = Math.random() * 16 | 0;
            const v = c == 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
          });
        };

        const now = new Date().toISOString();
        const { currentUser } = useAuthStore.getState();
        const userId = currentUser?.id || generateUUID();

        const apiPayload = {
          userId: userId,
          courseId: bookmark.courseId,
          topicId: bookmark.topicId || generateUUID(),
          resourceId: bookmark.resourceId,
          title: bookmark.title || 'Untitled Bookmark',
          tags: bookmark.tags || '',
          createdAt: now,
          updatedAt: now
        };

        const client = await apiClient.initialize();
        const response = await client.post('/bookmarks/add', apiPayload);

        // Create local bookmark object with correct page number
        // Extract ID from response (handling data.data.id structure which is standard for this API)
        const serverId = response.data?.data?.id || response.data?.data?.bookmarkId || response.data?.id || response.data?.bookmarkId;

        const newBookmark = {
          id: serverId || `bookmark-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          createdAt: now,
          ...bookmark,
          // Store the actual page number (1-based) for display
          pageNumber: bookmark.pageNumber,
          // Also store the resource index for navigation
          resourceIndex: bookmark.resourceIndex,
        };

        const updatedBookmarks = [...get().bookmarks, newBookmark];
        set({ bookmarks: updatedBookmarks });

        console.log('✅ Bookmark added successfully:', response.data);
        return newBookmark;
      } catch (error) {
        console.error('❌ Failed to add bookmark:', error);
        throw error;
      }
    },

    /**
     * Remove a bookmark
     * @param {string} bookmarkId - Bookmark ID
     */
    removeBookmark: async (bookmarkId) => {
      try {
        // Make API call to delete bookmark
        const client = await apiClient.initialize();
        await client.delete(`/bookmarks/delete/${bookmarkId}`);

        // Update local state after successful API call
        const updatedBookmarks = get().bookmarks.filter(
          (bookmark) => bookmark.id !== bookmarkId
        );
        set({ bookmarks: updatedBookmarks });

        console.log("🗑️ Bookmark removed:", bookmarkId);
      } catch (error) {
        console.error('❌ Failed to remove bookmark:', error);
        throw error; // Re-throw error since we're not using local storage fallback
      }
    },

    /**
     * Add an annotation
     * @param {Object} annotation - Annotation data
     */
    addAnnotation: (annotation) => {
      const newAnnotation = {
        id: `annotation-${Date.now()}-${Math.random()
          .toString(36)
          .substr(2, 9)}`,
        createdAt: new Date().toISOString(),
        ...annotation,
      };

      const updatedAnnotations = [...get().annotations, newAnnotation];
      set({ annotations: updatedAnnotations });

      console.log("✅ Annotation added:", newAnnotation.title);
      return newAnnotation;
    },

    /**
     * Update an annotation
     * @param {string} annotationId - Annotation ID
     * @param {Object} updates - Updates to apply
     */
    updateAnnotation: (annotationId, updates) => {
      const updatedAnnotations = get().annotations.map((annotation) =>
        annotation.id === annotationId
          ? { ...annotation, ...updates, updatedAt: new Date().toISOString() }
          : annotation
      );
      set({ annotations: updatedAnnotations });

      console.log("✅ Annotation updated:", annotationId);
    },

    /**
     * Remove an annotation
     * @param {string} annotationId - Annotation ID
     */
    removeAnnotation: (annotationId) => {
      const updatedAnnotations = get().annotations.filter(
        (annotation) => annotation.id !== annotationId
      );
      set({ annotations: updatedAnnotations });

      console.log("🗑️ Annotation removed:", annotationId);
    },

    /**
     * Get bookmarks for a specific topic/page
     * @param {string} topicId - Topic ID (optional)
     * @param {string} pageId - Page ID (optional)
     */
    getBookmarks: (topicId = null, pageId = null) => {
      const { bookmarks } = get();
      if (!topicId && !pageId) return bookmarks;

      return bookmarks.filter((bookmark) => {
        if (topicId && pageId) {
          return bookmark.topicId === topicId && bookmark.pageId === pageId;
        }
        if (topicId) {
          return bookmark.topicId === topicId;
        }
        if (pageId) {
          return bookmark.pageId === pageId;
        }
        return true;
      });
    },

    /**
     * Get annotations for a specific topic/page
     * @param {string} topicId - Topic ID (optional)
     * @param {string} pageId - Page ID (optional)
     */
    getAnnotations: (topicId = null, pageId = null) => {
      const { annotations } = get();
      if (!topicId && !pageId) return annotations;

      return annotations.filter((annotation) => {
        if (topicId && pageId) {
          return annotation.topicId === topicId && annotation.pageId === pageId;
        }
        if (topicId) {
          return annotation.topicId === topicId;
        }
        if (pageId) {
          return annotation.pageId === pageId;
        }
        return true;
      });
    },

    /**
     * Check if current page is bookmarked
     * @param {string} topicId - Topic ID
     * @param {string} pageId - Page ID
     */
    isPageBookmarked: (topicId, pageId) => {
      return get().bookmarks.some(
        (bookmark) => bookmark.topicId === topicId && bookmark.pageId === pageId
      );
    },

    /**
     * Check if topic is bookmarked (for API bookmarks without pageId)
     * @param {string} topicId - Topic ID
     */
    isTopicBookmarked: (topicId) => {
      return get().bookmarks.some(
        (bookmark) => bookmark.topicId === topicId
      );
    },

    /**
     * Check if a specific resource is bookmarked
     * @param {string} resourceId - Resource ID
     */
    isResourceBookmarked: (resourceId) => {
      return get().bookmarks.some(
        (bookmark) => bookmark.resourceId === resourceId
      );
    },

    /**
     * Get bookmark by resourceId
     * @param {string} resourceId - Resource ID
     */
    getBookmarkByResourceId: (resourceId) => {
      return get().bookmarks.find(
        (bookmark) => bookmark.resourceId === resourceId
      );
    },

    /**
     * Clear all bookmarks
     */
    clearBookmarks: () => {
      set({ bookmarks: [] });
    },

    /**
     * Fetch bookmarks from API
     * @param {string} resourceId - Resource ID
     */
    fetchBookmarks: async (resourceId) => {
      try {
        if (!resourceId) {
          console.warn('No resourceId provided for fetching bookmarks');
          return;
        }

        const client = await apiClient.initialize();
        const response = await client.get('/bookmarks/getAll', {
          params: { resourceId }
        });

        if (response.data && response.data.data) {
          // Transform API response to match local bookmark structure
          const apiBookmarks = response.data.data.map(bookmark => ({
            id: bookmark.id || bookmark.bookmarkId,
            topicId: bookmark.topicId,
            pageId: bookmark.pageId,
            resourceId: bookmark.resourceId,
            title: bookmark.title,
            tags: bookmark.tags,
            courseId: bookmark.courseId,
            userId: bookmark.userId,
            // Convert nested date array to ISO string
            createdAt: bookmark.createdAt ? new Date(
              bookmark.createdAt[0], // year
              bookmark.createdAt[1] - 1, // month (0-indexed)
              bookmark.createdAt[2], // day
              bookmark.createdAt[3] || 0, // hour
              bookmark.createdAt[4] || 0, // minute
              bookmark.createdAt[5] || 0, // second
              Math.floor((bookmark.createdAt[6] || 0) / 1000000) // nanoseconds to milliseconds
            ).toISOString() : new Date().toISOString(),
            pageNumber: bookmark.pageNumber || 1
          }));

          // Merge with existing bookmarks, avoiding duplicates
          const existingBookmarks = get().bookmarks;
          const mergedBookmarks = [...existingBookmarks];

          apiBookmarks.forEach(newBookmark => {
            const existingIndex = mergedBookmarks.findIndex(existing => existing.id === newBookmark.id);
            if (existingIndex === -1) {
              mergedBookmarks.push(newBookmark);
            } else {
              mergedBookmarks[existingIndex] = newBookmark;
            }
          });

          // Update store with merged bookmarks
          set({ bookmarks: mergedBookmarks });

          console.log('✅ Bookmarks fetched from API:', apiBookmarks.length);
          return apiBookmarks;
        }
      } catch (error) {
        console.error('❌ Failed to fetch bookmarks from API:', error);
        // Keep existing bookmarks on error
        return get().bookmarks;
      }
    },

    /**
     * Fetch bookmarks by courseId from API
     * @param {string} courseId - Course ID
     */
    fetchBookmarksByCourseId: async (courseId) => {
      try {
        if (!courseId) {
          console.warn('No courseId provided for fetching bookmarks');
          return;
        }

        const client = await apiClient.initialize();
        const response = await client.get('/bookmarks/getAll', {
          params: { courseId }
        });

        if (response.data && response.data.data) {
          // Transform API response to match local bookmark structure
          const apiBookmarks = response.data.data.map(bookmark => ({
            id: bookmark.id || bookmark.bookmarkId,
            topicId: bookmark.topicId,
            pageId: bookmark.pageId,
            resourceId: bookmark.resourceId,
            title: bookmark.title,
            tags: bookmark.tags,
            courseId: bookmark.courseId,
            userId: bookmark.userId,
            // Convert nested date array to ISO string
            createdAt: bookmark.createdAt ? new Date(
              bookmark.createdAt[0], // year
              bookmark.createdAt[1] - 1, // month (0-indexed)
              bookmark.createdAt[2], // day
              bookmark.createdAt[3] || 0, // hour
              bookmark.createdAt[4] || 0, // minute
              bookmark.createdAt[5] || 0, // second
              Math.floor((bookmark.createdAt[6] || 0) / 1000000) // nanoseconds to milliseconds
            ).toISOString() : new Date().toISOString(),
            pageNumber: bookmark.pageNumber || 1
          }));

          // Replace existing bookmarks with fetched ones
          set({ bookmarks: apiBookmarks });

          console.log('✅ Bookmarks fetched by courseId from API:', apiBookmarks.length);
          return apiBookmarks;
        }
      } catch (error) {
        console.error('❌ Failed to fetch bookmarks by courseId from API:', error);
        // Keep existing bookmarks on error
        return get().bookmarks;
      }
    },

    /**
     * Clear all bookmarks and annotations
     */
    clearAll: () => {
      set({ bookmarks: [], annotations: [] });
      console.log("🧹 All bookmarks and annotations cleared");
    },
  };
});
